import { WeightUnit } from "@redotech/cockroachdb-codegen/tables/product/weight-unit";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import { type CarrierAndService } from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import {
  convertWeight,
  Weight,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import {
  getPackageDimensionInInches,
  isCustomParcel,
  Parcel,
} from "@redotech/redo-fulfillment-parcel-common/parcel";

export const DIMENSIONAL_WEIGHT_THRESHOLD_PERCENT = 0.01;

export interface DimensionalWeightThresholdWarning {
  dimensionalWeightPounds: number;
  divisor: number;
  percentFromThreshold: number;
}

type ParcelWithContentWeight = Parcel & {
  parcelAndContentWeight?: Weight | null;
};

interface DimensionalWeightConfig {
  divisor: number;
  minimumDimensionalVolumeCubicInches?: number;
}

const STANDARD_DIMENSIONAL_WEIGHT_DIVISOR = 139;
const USPS_DIMENSIONAL_WEIGHT_DIVISOR = 166;
const USPS_DIMENSIONAL_WEIGHT_MINIMUM_VOLUME_CUBIC_INCHES = 1728;

export function getDimensionalWeightThresholdWarning({
  parcel,
  selectedRate,
}: {
  parcel: ParcelWithContentWeight | null;
  selectedRate?: CarrierAndService | null;
}): DimensionalWeightThresholdWarning | null {
  if (!parcel || !isCustomParcel(parcel)) {
    return null;
  }

  const { height, length, width } = getPackageDimensionInInches(parcel);
  if (height <= 0 || length <= 0 || width <= 0) {
    return null;
  }

  const actualWeightPounds = getRoundedPackageWeightPounds(
    parcel.parcelAndContentWeight,
  );
  const volumeCubicInches = height * length * width;

  const config = selectedRate
    ? DIMENSIONAL_WEIGHT_CONFIG_BY_CARRIER[selectedRate.carrier]
    : null;
  if (!config) {
    return null;
  }

  return getWarningForConfig({ actualWeightPounds, config, volumeCubicInches });
}

const STANDARD_DIMENSIONAL_WEIGHT_CONFIG = {
  divisor: STANDARD_DIMENSIONAL_WEIGHT_DIVISOR,
} satisfies DimensionalWeightConfig;

const USPS_DIMENSIONAL_WEIGHT_CONFIG = {
  divisor: USPS_DIMENSIONAL_WEIGHT_DIVISOR,
  minimumDimensionalVolumeCubicInches:
    USPS_DIMENSIONAL_WEIGHT_MINIMUM_VOLUME_CUBIC_INCHES,
} satisfies DimensionalWeightConfig;

// Keep this exhaustive so every new carrier must explicitly choose whether
// dimensional-weight threshold warnings are implemented.
// A null value means we have not implemented carrier-specific thresholds yet.
const DIMENSIONAL_WEIGHT_CONFIG_BY_CARRIER: Record<
  Carriers,
  DimensionalWeightConfig | null
> = {
  [Carriers.FEDEX]: STANDARD_DIMENSIONAL_WEIGHT_CONFIG,
  [Carriers.FEDEX_SMART_POST]: STANDARD_DIMENSIONAL_WEIGHT_CONFIG,
  [Carriers.UPS]: STANDARD_DIMENSIONAL_WEIGHT_CONFIG,
  [Carriers.UPS_SUREPOST]: STANDARD_DIMENSIONAL_WEIGHT_CONFIG,
  [Carriers.UPS_MAIL_INNOVATIONS]: STANDARD_DIMENSIONAL_WEIGHT_CONFIG,
  [Carriers.USPS]: USPS_DIMENSIONAL_WEIGHT_CONFIG,
  [Carriers.USPS_RETURNS]: USPS_DIMENSIONAL_WEIGHT_CONFIG,
  [Carriers.USPS_SHIP]: USPS_DIMENSIONAL_WEIGHT_CONFIG,
  [Carriers.DHL_EXPRESS]: STANDARD_DIMENSIONAL_WEIGHT_CONFIG,
  [Carriers.AMAZON]: null,
  [Carriers.DHL_ECS]: null,
  [Carriers.VEHO]: null,
  [Carriers.APC]: null,
  [Carriers.FIRST_MILE]: null,
  [Carriers.AUSTRALIA_POST]: null,
  [Carriers.CANADA_POST]: null,
  [Carriers.GLS]: null,
  [Carriers.ASENDIA]: null,
  [Carriers.ROYAL_MAIL_V3]: null,
  [Carriers.SPEE_DEE]: null,
  [Carriers.PASSPORT_GLOBAL]: null,
  [Carriers.EPOST_GLOBAL]: null,
  [Carriers.LANDMARK_GLOBAL]: null,
  [Carriers.ETA_EXPRESS]: null,
  [Carriers.ONTRAC]: null,
  [Carriers.GLOBALE]: null,
  [Carriers.PUROLATOR]: null,
  [Carriers.UNIUNI]: null,
  [Carriers.STALLION_EXPRESS]: null,
  [Carriers.ECOM_EXPRESS]: null,
  [Carriers.CHITCHATS]: null,
  [Carriers.POST_NL]: null,
  [Carriers.INTELCOM]: null,
  [Carriers.FLEET_OPTICS]: null,
  [Carriers.ICS_COURIER]: null,
  [Carriers.WHIZ_DELIVERY]: null,
  [Carriers.TIKTOK_SHOP]: null,
  [Carriers.CANPAR_EXPRESS]: null,
};

function getWarningForConfig({
  actualWeightPounds,
  config,
  volumeCubicInches,
}: {
  actualWeightPounds: number | null;
  config: DimensionalWeightConfig;
  volumeCubicInches: number;
}): DimensionalWeightThresholdWarning | null {
  const { divisor, minimumDimensionalVolumeCubicInches } = config;
  if (
    minimumDimensionalVolumeCubicInches &&
    volumeCubicInches <= minimumDimensionalVolumeCubicInches
  ) {
    return getMinimumVolumeWarning({
      actualWeightPounds,
      divisor,
      minimumDimensionalVolumeCubicInches,
      volumeCubicInches,
    });
  }

  const dimensionalWeightPounds = Math.max(
    1,
    Math.ceil(volumeCubicInches / divisor),
  );
  const currentBillableWeightPounds = Math.max(
    actualWeightPounds ?? 0,
    dimensionalWeightPounds,
  );

  const thresholds = [
    Math.max(
      minimumDimensionalVolumeCubicInches ?? 0,
      (dimensionalWeightPounds - 1) * divisor,
    ),
    dimensionalWeightPounds * divisor,
  ].filter((threshold) => threshold > 0);

  const closestThreshold = thresholds
    .map((threshold) => ({
      percentFromThreshold: Math.abs(volumeCubicInches - threshold) / threshold,
      threshold,
    }))
    .sort((a, b) => a.percentFromThreshold - b.percentFromThreshold)[0];

  if (
    !closestThreshold ||
    closestThreshold.percentFromThreshold > DIMENSIONAL_WEIGHT_THRESHOLD_PERCENT
  ) {
    return null;
  }

  const adjacentDimensionalWeightPounds =
    closestThreshold.threshold < volumeCubicInches
      ? dimensionalWeightPounds - 1
      : dimensionalWeightPounds + 1;
  const adjacentBillableWeightPounds = Math.max(
    actualWeightPounds ?? 0,
    adjacentDimensionalWeightPounds,
  );

  if (currentBillableWeightPounds === adjacentBillableWeightPounds) {
    return null;
  }

  return {
    dimensionalWeightPounds,
    divisor,
    percentFromThreshold: closestThreshold.percentFromThreshold,
  };
}

function getMinimumVolumeWarning({
  actualWeightPounds,
  divisor,
  minimumDimensionalVolumeCubicInches,
  volumeCubicInches,
}: {
  actualWeightPounds: number | null;
  divisor: number;
  minimumDimensionalVolumeCubicInches: number;
  volumeCubicInches: number;
}): DimensionalWeightThresholdWarning | null {
  const percentFromThreshold =
    Math.abs(volumeCubicInches - minimumDimensionalVolumeCubicInches) /
    minimumDimensionalVolumeCubicInches;
  if (percentFromThreshold > DIMENSIONAL_WEIGHT_THRESHOLD_PERCENT) {
    return null;
  }

  const dimensionalWeightPounds = Math.max(
    1,
    Math.ceil((minimumDimensionalVolumeCubicInches + 1) / divisor),
  );
  const adjacentBillableWeightPounds = Math.max(
    actualWeightPounds ?? 0,
    dimensionalWeightPounds,
  );

  if ((actualWeightPounds ?? 0) === adjacentBillableWeightPounds) {
    return null;
  }

  return { dimensionalWeightPounds, divisor, percentFromThreshold };
}

function getRoundedPackageWeightPounds(weight?: Weight | null): number | null {
  if (!weight) {
    return null;
  }
  return Math.max(1, Math.ceil(convertWeight(weight, WeightUnit.Lb).value));
}
